import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { X, KeyRound, ArrowRight } from "lucide-react";
import DuckyAvatar from "@/components/app/DuckyAvatar";

/**
 * Hidden admin access modal.
 * Opens two ways:
 *   1. Ctrl/Cmd + Shift + C — global keyboard shortcut
 *   2. Custom event `cq:open-secret` — dispatched by clicking the word "Pizza" in the tracker
 *
 * Passwords live server-side ONLY. Backend endpoint /api/auth/secret-code
 * validates the code and returns a real JWT + redirect target.
 */
export default function SecretAccessModal() {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const nav = useNavigate();
  const { setUser } = useAuth();

  useEffect(() => {
    const onKey = (e) => {
      // Ctrl+Shift+C on Windows/Linux, Cmd+Shift+C on Mac
      const cmdKey = e.ctrlKey || e.metaKey;
      if (cmdKey && e.shiftKey && (e.key === "C" || e.key === "c")) {
        // Don't hijack native devtools shortcut when it's Ctrl+Shift+C on macOS in specific dev modes
        e.preventDefault();
        setOpen((o) => !o);
      }
      if (e.key === "Escape" && open) setOpen(false);
    };
    const onEvt = () => setOpen(true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("cq:open-secret", onEvt);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("cq:open-secret", onEvt);
    };
  }, [open]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 60);
    else { setCode(""); setErr(""); }
  }, [open]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!code.trim() || busy) return;
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/auth/secret-code", { code: code.trim() });
      // Real JWT — the app treats us as a fully-logged-in user
      localStorage.setItem("cq_token", data.token);
      setUser({
        id: null,
        role: data.role,
        name: data.name,
        email: data.email,
        avatar_url: data.avatar_url,
        title: data.title,
        must_change_password: data.must_change_password,
      });
      setOpen(false);
      nav(data.redirect || "/");
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || "Access denied. Please verify your credentials.");
    } finally { setBusy(false); }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center p-4 animate-in fade-in duration-200"
      role="dialog"
      aria-modal="true"
      aria-label="Restricted access"
      data-testid="secret-access-modal"
    >
      <div className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm" onClick={() => setOpen(false)} />
      <form
        onSubmit={submit}
        className="relative w-full max-w-sm rounded-3xl bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 border border-amber-500/25 shadow-2xl shadow-amber-500/10 p-7 text-white"
      >
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label="Close"
          className="absolute top-3 right-3 h-8 w-8 rounded-full flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition"
          data-testid="secret-close"
        >
          <X size={16} />
        </button>

        <div className="flex items-center gap-3 mb-1">
          <div className="h-10 w-10 rounded-xl bg-amber-500/20 border border-amber-500/40 flex items-center justify-center">
            <KeyRound size={17} className="text-amber-300" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] font-bold text-amber-300">Restricted</p>
            <p className="font-display font-black text-lg leading-none mt-0.5">Access code</p>
          </div>
        </div>

        <p className="text-xs text-slate-400 mt-4 leading-relaxed">
          Enter your access code to continue. Codes are not visible in the UI.
        </p>

        <input
          ref={inputRef}
          type="password"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          autoComplete="off"
          spellCheck="false"
          placeholder="••••••"
          disabled={busy}
          className="mt-4 w-full h-12 px-4 rounded-xl bg-slate-950/80 border border-slate-700 focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-400/30 text-white font-mono tracking-[0.35em] text-center"
          data-testid="secret-input"
        />
        {err && <p className="text-xs text-red-400 mt-3 text-center font-semibold" data-testid="secret-error">{err}</p>}

        <button
          type="submit"
          disabled={busy || !code.trim()}
          className="mt-4 w-full h-12 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-black inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition"
          data-testid="secret-submit"
        >
          {busy ? "Verifying…" : (<>Enter <ArrowRight size={15} /></>)}
        </button>

        <p className="text-[10px] text-slate-500 mt-5 text-center tracking-wide inline-flex items-center justify-center gap-1.5 w-full">
          <DuckyAvatar size={16} ring={false} bare />
          Credit Quack · Enterprise-grade auth
        </p>
      </form>
    </div>
  );
}
